import { type JanusCtx, type Row, handBack, insertRoll, itemField, record, upper } from './context.js';
import { gateItemPath, targetImmuneToAbilityId } from './immune-to-ability.js';
import { checkOpposingTeam } from './check-opposing-team.js';
import { casterLayerProvider } from '../actions/cast-range-gate.js';
import type { AbilityPreconditionReaders } from '../actions/ability-preconditions.js';
import { forbiddenTargetStatusFailure, forbiddenTargetStatusMessage, requiredTargetStatusFailure, requiredTargetStatusMessage, statusDisplayName } from './statuses/target-status-gate.js';
import { targetGateWarning } from './is-target-valid-for-effect.js';
import { passiveConditionsMet } from '../reactions/trigger-dispatch.js';
import { janusCtxTriggerReaders } from '../reactions/janus-ctx-trigger-readers.js';

/** Same provider walk as PHP: passive and feat grants need not be promoted to the root. */
export function hostileSpellImmunity(ctx: JanusCtx, ability: Row, caster: Row, target: Row): boolean {
  if (!['spell', 'curse'].includes(String(itemField(ability, 'item_type') ?? '')) || !checkOpposingTeam(ctx, caster, target)) return false;
  const read = { ...ctx.read, resolveItem: (entry: unknown) => {
    const row = record(entry);
    return ctx.read.definition(upper(typeof entry === 'string' ? entry : row?.ability_id ?? itemField(row, 'item_id')));
  } } as unknown as AbilityPreconditionReaders;
  const provider = casterLayerProvider(read, target, 'immune_to_hostile_spells');
  if (provider === undefined) handBack('immune_to_hostile_spells:provider-unproved');
  return provider !== null;
}

function ids(entries: unknown[]): Set<string> {
  return new Set(entries.map((entry) => upper(typeof entry === 'string' ? entry.split('|')[0] : itemField(record(entry), 'item_id'))).filter(Boolean));
}

/** nullify_post_effect_conditions.php: per-recipient immunity, classification and status gates. */
export function nullifyPostEffectConditions(ctx: JanusCtx, ability: Row, caster: Row, target: Row): boolean {
  // PHP unions these four division whitelists AFTER the blacklist/type checks. Until the
  // classification-specific failure metadata is held, preserve that contract before drawing.
  if (['ability_works_only_against_creature_sub_type', 'ability_works_only_against_creature_lineage',
    'ability_works_only_against_creature_ancestry', 'ability_works_only_against_creature_grouping']
    .some(key => ctx.read.has(ability, key))) handBack('nullify_post_effect_conditions:sub-slot-whitelist');
  let met = true;
  if (ctx.trigger && !passiveConditionsMet(janusCtxTriggerReaders(ctx), ability, ctx.triggerIncitingTarget ?? target)) met = false;
  const immuneId = targetImmuneToAbilityId(ctx, ability, target);
  if (immuneId) {
    met = false;
    const iconToken = `ft_gate_icons:${immuneId}`;
    const pathToken = gateItemPath(ctx, ability);
    insertRoll(ctx, { roll_type_1: 'post_effect_condition_failed', roll_type_2: 'target_immune_to_ability',
      roll_item_id: immuneId, by: caster, to: target, rolls_full: pathToken ? `${iconToken}\n${pathToken}` : iconToken });
  }
  const spellLike = ['spell', 'curse'].includes(String(itemField(ability, 'item_type') ?? ''));
  const immune = ctx.read.value(target, 'immune_to_spells');
  for (const [key, blocks] of [
    ['immune_to_spells', spellLike && !(immune == null || immune === '' || immune === 0 || immune === '0' || immune === false || (Array.isArray(immune) && !immune.length))],
    ['immune_to_hostile_spells', hostileSpellImmunity(ctx, ability, caster, target)],
  ] as const) {
    if (!blocks) continue;
    met = false;
    const spellId = upper(itemField(ability, 'item_id'));
    const path = gateItemPath(ctx, ability);
    insertRoll(ctx, { roll_type_1: 'post_effect_condition_failed', roll_type_2: key,
      roll_item_id: spellId, by: caster, to: target, rolls_full: [`ft_gate_icons:${spellId}`, path].filter(Boolean).join('\n') });
  }
  for (const [key, targetKey, only] of [
    ['ability_works_only_against_creature_type', 'creature_type', true],
    ['ability_does_not_work_against_creature_type', 'creature_type', false],
    ['ability_does_not_work_against_creature_sub_type', 'creature_sub_type', false],
    ['ability_does_not_work_against_creature_lineage', 'creature_sub_type', false],
    ['ability_does_not_work_against_creature_ancestry', 'creature_sub_type', false],
    ['ability_does_not_work_against_creature_grouping', 'creature_sub_type', false],
  ] as const) {
    if (!ctx.read.has(ability, key)) continue;
    const authoredEntries = ctx.read.entries(ability, key);
    const targetEntries = ctx.read.entries(target, targetKey);
    if (!targetEntries.length) handBack('nullify_post_effect_conditions:classification-not-held');
    const authored = ids(authoredEntries);
    const targetIds = ids(targetEntries);
    if (!authored.size || authored.size !== authoredEntries.length || targetIds.size !== targetEntries.length
        || [...authored, ...targetIds].some(id => !/^[0-9A-F]{8}(?:-[0-9A-F]{4}){3}-[0-9A-F]{12}$/.test(id)))
      handBack('nullify_post_effect_conditions:malformed-classification');
    const matches = [...targetIds].some((id) => authored.has(id));
    if (only ? !matches : matches) {
      met = false;
      const authoredIds = [...authored], matching = [...targetIds].filter(id => authored.has(id));
      const logId = only ? authoredIds[0]! : (matching[0] ?? [...targetIds][0] ?? authoredIds[0])!;
      const icons = [...authoredIds];
      if (!only && !icons.includes(logId)) icons.push(logId);
      const path = gateItemPath(ctx, ability);
      const tokens = ['ft_gate_icons:' + icons.join('|'), path].filter(Boolean).join('\n');
      insertRoll(ctx, { roll_type_1: 'post_effect_condition_failed', roll_type_2: key.replace('ability_', ''),
        roll_item_id: logId, by: caster, to: target, rolls_full: tokens });
    }
  }
  // A triggered passive applies to itself but its status predicate concerns the inciting creature.
  const statusTarget = ctx.triggerIncitingTarget ?? target;
  for (const layer of [record(ability.attack_ability_arr), ability]) {
    if (!layer) continue;
    const path = gateItemPath(ctx, layer);
    const log = (key: string, ids: string[], message: string, itemId = ids[0]!): void => {
      met = false;
      targetGateWarning(ctx, message, { includeTriggers: true });
      insertRoll(ctx, { roll_type_1: 'post_effect_condition_failed', roll_type_2: key,
        roll_item_id: itemId, by: caster, to: target,
        rolls_full: [`ft_gate_icons:${ids.join('|')}`, path].filter(Boolean).join('\n') });
      ctx.rows[ctx.rows.length - 1]!.roll_private = 0;
    };
    const failure = requiredTargetStatusFailure(ctx, layer, caster, statusTarget, 'nullify_post_effect_conditions');
    if (failure) log('if_target_has_status_effect_active', failure.ids, requiredTargetStatusMessage(failure.names, true));
    const blocked = forbiddenTargetStatusFailure(ctx, layer, statusTarget, 'nullify_post_effect_conditions');
    if (blocked) log('if_target_has_status_effect_not_active', blocked.ids,
      forbiddenTargetStatusMessage(statusDisplayName(ctx, blocked.id, 'nullify_post_effect_conditions'), true), blocked.id);
  }
  return met;
}
